import EventEmitter from "eventemitter3";
import mapboxgl from 'mapbox-gl';

export const MANAGE_MAP_EVENT = {
    'CLICK_PIN' : '__mm_click_pin',
    'CLOSE_POPUP' : '__mm_close_popup'
}

export const MANAGE_POPUP_EVENT = {
    'CLOSE' : '__mp_close',
}

/*
    Class MapManager
    Manage Map
    Params : {
       {Object} dataMap - data of map :
       {
            container : DOM - container of map,
            accessToken,
            style,
            zoom : init zoom,
            minZoom,
            navigationControl : string - position of navigation control (ex : 'bottom-right'),
            navigationCustom : {
              'zoom' : DOM button zoom-in,
              'unzoom' : DOM button zoom-out
            },
            scrollZoom: boolean - Allow scroll zoom,
            center: coordinate array to center the map at its initialization (ex : [ 2.3488 , 48.8534 ]),
            disableDragRotation: boolean
        }

       {Object} dataClusters - data of clusters :
       {
            list : [
                {
                   id: string - name of cluster (ex : 'name-cluster'),
                   markers: json - list of marker with their properties,
                   clusterMaxZoom: number,
                   clusterRadius: number,
                   clusterCircle: {
                        color,
                        stroke: {
                            width: number,
                            color: hexa
                        },
                        radius
                   },
                   clusterCount: {
                        size: number,
                        color: hexa,
                        font: string font
                   },
                   clusterPoint: {
                        pinImage: url of pin image,
                        size: number
                   }
                }
            ]
       }
    }
*/
export default class MapManager extends EventEmitter {


    constructor( { dataMap = {}, dataClusters = {} } = {} ) {

        super();

        this._dataMap = dataMap;
        this._params = {
            maxZoom : 15
        }
        this._clustersIsValid = true;

        this._dataClusters = {
            list: ( dataClusters.hasOwnProperty('list') && typeof dataClusters.list === "object" ) ? dataClusters.list : false,
        }

        if ( this._checkRequiredParams() == false )
            return;

        this._checkParamsMap();

        this._initClusters();

        if( this._clustersIsValid ) {
            this._initMap();
            this._initPopup();
        }
    }

    /**
     * Check if the mandatory parameters to create a map are well informed
     * @return {Boolean}
     */
    _checkRequiredParams() {

        let styleConsole = 'background: #222; color: #ff8c8c'; // green : bada55

        if( this._dataMap.hasOwnProperty('container') == false || this._dataMap.container == '' ) {
            console.warn('%c The container of the map is not defined ', styleConsole);
            return false;
        }

        if( this._dataMap.hasOwnProperty('accessToken') == false || this._dataMap.accessToken == '' ) {
            console.warn('%c the map box API token is not defined ', styleConsole);
            return false;
        }

        if ( this._dataClusters.list == false ) {
            console.warn('%c No cluster has been defined ', styleConsole);
            return false;
        }

        if(
            this._dataMap.hasOwnProperty('navigationControl') == false
            && this._dataMap.hasOwnProperty('navigationCustom')
            && typeof this._dataMap['navigationCustom'] == "object"
        )
        {
            if( this._dataMap.navigationCustom.hasOwnProperty('zoom') == false ) {
                console.warn('%c For custom navigation, you must set a DOM element to "zoom in" when users click on it: "zoom" attribute ', styleConsole);
                return false;
            }

            if( this._dataMap.navigationCustom.hasOwnProperty('unzoom') == false ) {
                console.warn('%c For custom navigation, you must set a DOM element to "zoom out" when users click on it: "unzoom" attribute ', styleConsole);
                return false;
            }
        }
    }

    /**
     * Check if the optional parameters are informed in and if not, set the default parameters
     */
    _checkParamsMap() {

        this.container = this._dataMap.container;

        this._datasMap = {

            accessToken : this._dataMap.accessToken,

            style : ( this._dataMap.hasOwnProperty('style') && this._dataMap.style != '' ) ? this._dataMap.style : 'mapbox://styles/mapbox/streets-v9',

            center : ( this._dataMap.hasOwnProperty('center') && Array.isArray( this._dataMap.center ) && this._dataMap.center.length == 2 ) ? this._dataMap.center : false,

            zoom : ( this._dataMap.hasOwnProperty('zoom') && Number.isInteger( this._dataMap.zoom ) ) ? this._dataMap.zoom : 2,

            minZoom : ( this._dataMap.hasOwnProperty('minZoom') && Number.isInteger( this._dataMap.minZoom ) ) ? this._dataMap.minZoom : 1,

            navigationControl : ( this._dataMap.hasOwnProperty('navigationControl') && this._dataMap.navigationControl != '' ) ? this._dataMap.navigationControl : false,

            scrollZoom : ( this._dataMap.hasOwnProperty('scrollZoom') && typeof this._dataMap.scrollZoom == 'boolean' ) ? this._dataMap.scrollZoom : true,

            disableDragRotation : ( this._dataMap.hasOwnProperty('disableDragRotation') && typeof this._dataMap.disableDragRotation == 'boolean' ) ? this._dataMap.disableDragRotation : false,
        }

        if(
            this._datasMap.navigationControl == false
            && this._dataMap.hasOwnProperty('navigationCustom')
            && this._dataMap['navigationCustom'].hasOwnProperty('zoom')
            && this._dataMap['navigationCustom']['zoom'] != null
            && this._dataMap['navigationCustom'].hasOwnProperty('unzoom')
            && this._dataMap['navigationCustom']['unzoom'] != null
        )
        {
            this._datasMap.navigationCustom = {
                'zoom': this._dataMap['navigationCustom']['zoom'],
                'unzoom': this._dataMap['navigationCustom']['unzoom']
            }
        }
    }

    /**
     * Create clusters
     */
    _initClusters() {

        this._clusterManagers = [];

        for( let cluster in this._dataClusters.list ) {

            let clusterItem = new ClusterManager({
                dataCluster : this._dataClusters.list[cluster]
            });

            /** Check if cluster is valid **/
            if ( clusterItem.requiredParamsIsValid == false )
                this._clustersIsValid = false;
            else
                this._clusterManagers.push( clusterItem );
        }
    }

    /**
     * Create map
     */
    _initMap() {

        mapboxgl.accessToken = this._datasMap.accessToken;

        this._map = new mapboxgl.Map({
            container: this.container,
            style: this._datasMap.style,
            zoom: this._datasMap.zoom,
            center: this._datasMap.center || [0,0],
            minZoom: this._datasMap.minZoom
        });

        if( this._datasMap.navigationControl ) {
            let nav = new mapboxgl.NavigationControl();
            this._map.addControl(nav, this._datasMap.navigationControl);
        }

        if( this._datasMap.scrollZoom == false )
            this._map.scrollZoom.disable();

        if( this._datasMap.disableDragRotation == true ) {
            this._map.dragRotate.disable();
            this._map.touchZoomRotate.disableRotation();
        }

        this._map.on('load', function() {

            this._addClusters();
            this._addLayers();

            /** If no centering is requested for the map, it is self-centered relative to the pin present on the map **/
            if( this._datasMap.center == false )
                this._updateCentering();

            this._bind();

        }.bind(this));
    }

    /**
     * Create popup
     */
    _initPopup() {
        this._popupManager = new PopupManager();
    }

    /**
     * Add clusters on map
     */
    _addClusters() {

        for ( let cluster in this._clusterManagers ) {

            let element = this._clusterManagers[cluster];

            this._map.addSource("cluster-" + element.data.id, {
                type: "geojson",
                data: element.geojson,
                cluster: true,
                clusterMaxZoom: element.data.clusterMaxZoom,
                clusterRadius: element.data.clusterRadius
            });

        }
    }

    /**
     * For each cluster, add Layer type circle and symbol (circle, count, image pin)
     */
    _addLayers() {

        for ( let cluster in this._clusterManagers ) {

            let element = this._clusterManagers[cluster];

            this._map.addLayer({
                id: "clusters-" + element.data.id,
                type: "circle",
                source: "cluster-" + element.data.id,
                filter: ["has", "point_count"],
                paint: {
                    "circle-color": element.data.clusterCircle.color,
                    "circle-radius": element.data.clusterCircle.radius,
                    "circle-stroke-width": element.data.clusterCircle.stroke.width,
                    "circle-stroke-color": element.data.clusterCircle.stroke.color,
                }
            });

            this._map.addLayer({
                id: "cluster-count-" + element.data.id,
                type: "symbol",
                source: "cluster-" + element.data.id,
                filter: ["has", "point_count"],
                layout: {
                    "text-field": "{point_count_abbreviated}",
                    "text-font": element.data.clusterCount.font,
                    "text-size": element.data.clusterCount.size
                },
                paint: {
                    "text-color": element.data.clusterCount.color
                }
            });

            this._map.loadImage( element.data.clusterPoint.pinImage, ( error, image ) => {

                if ( error ) throw error;

                this._map.addImage('pin-image-' + element.data.id, image);

                this._map.addLayer({
                    id: "unclustered-point-" + element.data.id,
                    type: "symbol",
                    source: "cluster-" + element.data.id,
                    filter: ["!", ["has", "point_count"]],
                    layout: {
                        "icon-image": 'pin-image-' + element.data.id,
                        "icon-size": element.data.clusterPoint.size
                    }
                });
            });
        }
    }

    /**
     * Auto-centering the map according to the position of the clusters that are above
     */
    _updateCentering() {

        let bounds = new mapboxgl.LngLatBounds();

        for ( let cluster in this._clusterManagers ) {

            let element = this._clusterManagers[cluster];

            element.geojson.features.forEach(function(feature) {
                bounds.extend(feature.geometry.coordinates);
            });

        }
        this._map.setCenter(bounds.getCenter());
        this._map.fitBounds(bounds, {
            padding: {top: 50, bottom: 50, left: 50, right: 50}
        });
    }

    _bind() {

        this._listerners = {};
        this._listerners._clickCluster = [];
        this._listerners._clickPin = [];
        this._listerners._mouseEnterCluster = this._mouseEnterCluster.bind(this);
        this._listerners._mouseLeaveCluster = this._mouseLeaveCluster.bind(this);
        this._listerners._navigationZoomIn = this._navigationZoomIn.bind(this);
        this._listerners._navigationZoomOut = this._navigationZoomOut.bind(this);


        for ( let cluster in this._clusterManagers ) {

            let element = this._clusterManagers[cluster];

            this._listerners._clickCluster[element] = this._clickCluster.bind(this, element.data.id);
            this._listerners._clickPin[element] = this._clickPin.bind(this, element.data.id);

            /** Click on a cluster circle **/
            this._map.on('click', 'clusters-' + element.data.id, this._listerners._clickCluster[element] );
            /** Mouse enter on a cluster circle **/
            this._map.on('mouseenter', 'clusters-' + element.data.id, this._listerners._mouseEnterCluster);
            /** Mouse leave on a cluster circle **/
            this._map.on('mouseleave', 'clusters-' + element.data.id, this._listerners._mouseLeaveCluster);
            /** Click on a pin **/
            this._map.on('click', 'unclustered-point-' + element.data.id, this._listerners._clickPin[element] );
        }

        /** For a custom navigation **/
        if( this._datasMap.navigationCustom ) {
            this._datasMap.navigationCustom.zoom.addEventListener('click', this._listerners._navigationZoomIn);
            this._datasMap.navigationCustom.unzoom.addEventListener('click', this._listerners._navigationZoomOut);
        }

        if( this._popupManager ) {
            this._popupManager.on( MANAGE_POPUP_EVENT.CLOSE, () => {
                this.emit( MANAGE_MAP_EVENT.CLOSE_POPUP );
            } );
        }
    }

    unbind() {}

    /**
     * Called when user clicks on a cluster : the map focuses on
     * @param {Number} idCluster - cluster id that was clicked
     * @param {Event} e - event
     */
    _clickCluster( idCluster, e ) {

        let features = this._map.queryRenderedFeatures(e.point, { layers: ['clusters-' + idCluster] });
        let clusterId = features[0].properties.cluster_id;

        this._map.getSource('cluster-' + idCluster).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err)
                return;

            this._map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom
            });
        });
    }

    /**
     * Called when the user's mouse is on the cluster
     */
    _mouseEnterCluster() {
        this._map.getCanvas().style.cursor = 'pointer';
    }

    /**
     * Called when the user's mouse leaves the cluster
     */
    _mouseLeaveCluster() {
        this._map.getCanvas().style.cursor = '';
    }

    /**
     * Called when user clicks on a pin : map focuses on && popup display
     * @param {Number} idCluster - id of the cluster to which the pin belongs
     * @param {Event} e - event
     */
    _clickPin( idCluster, e ) {

        let features = this._map.queryRenderedFeatures(e.point, { layers: ['unclustered-point-' + idCluster] }); // let lngLat = e.lngLat;

        let currentZoom = this._map.getZoom();
        let nextZoom = ( currentZoom >= this._params.maxZoom ) ? currentZoom : currentZoom + 1;

        this._map.easeTo({
            center: features[0].geometry.coordinates, //[lngLat.lng, lngLat.lat],
            // zoom: nextZoom
        });

        this.emit( MANAGE_MAP_EVENT.CLICK_PIN );

        this._popupManager.open( features[0].geometry.coordinates, features[0].properties.popup, this._map )
    }

    /**
     * Custom Navigation
     * Called when the user clicks the "zoom in" button
     * @param {Event} e - event
     */
    _navigationZoomIn( e ) {
        let currentZoom = this._map.getZoom();
        let nextZoom = ( currentZoom >= this._params.maxZoom ) ? currentZoom : currentZoom + 1;

        this._map.easeTo({
            zoom: nextZoom
        });
    }

    /**
     * Custom Navigation
     * Called when the user clicks the "zoom out" button
     * @param {Event} e - event
     */
    _navigationZoomOut( e ) {
        let currentZoom = this._map.getZoom();
        let nextZoom = currentZoom - 1;

        this._map.easeTo({
            zoom: nextZoom
        });
    }
}

/*
    Class ClusterManager
    Manage cluster
    Params : {
       {Object} dataCluster - data of cluster
    }
*/
class ClusterManager {

    constructor( { dataCluster = {} } ) {

        /** Default params **/
        this._params = {
            clusterMaxZoomDefault: 3,
            clusterRadiusDefault: 30,
            clusterCircle: {
                colorDefault: '#ffffff',
                radiusDefault: 8,
                strokeDefault: {
                    width: 0,
                    color: '#ffffff'
                }
            },
            clusterCount: {
                fontDefault: ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                sizeDefault: 12,
                colorDefault: '#ffffff'
            },
            clusterPoint : {
                sizeDefault: 1
            },
        }

        this.data = dataCluster;

        this.requiredParamsIsValid = this._checkRequiredParams();

        if ( this.requiredParamsIsValid == false )
            return;

        this._checkDefaultParams();

        this._initDataJSON();
    }

    /**
     * Check if the mandatory parameters to create a cluster are well informed
     * @return {Boolean}
     */
    _checkRequiredParams() {

        let styleConsole = 'background: #222; color: #ff8c8c'; // green : bada55

        if ( typeof this.data === "object" ) {

            if (
                ( this.data.hasOwnProperty('id') == false )
                || ( this.data['id'] == '' )
            ) {
                console.warn('%c All clusters must have an id ', styleConsole);
                return false;
            }

            if (
                ( this.data.hasOwnProperty('markers') == false )
                || ( typeof this.data['markers'] !== "object" )
            ) {
                console.warn('%c All clusters must have markers ', styleConsole);
                return false;
            }

            if(
                this.data.hasOwnProperty('clusterPoint') == false
            ) {
                console.warn('%c All clusters must have a clusterPoint object => clusterPoint : { pinImage, size } ', styleConsole);
                return false;
            }

            if (
                ( this.data.clusterPoint.hasOwnProperty('pinImage') == false )
                || ( this.data.clusterPoint['pinImage'] == '' )
                || ( typeof this.data.clusterPoint['pinImage'] != 'string' )
            ) {
                console.warn('%c All clusters must have a pin image ', styleConsole);
                return false;
            }
        }

        /**
         Markers in Cluster
         **/
        let markerIsGeolocate = true;
        for( let marker in this.data.markers ) {

            let element = this.data.markers[marker];

            if (
                ( element.hasOwnProperty('map_geolocation_lat') == false )
                || ( element['map_geolocation_lat'] == '' )
            ) {
                console.warn('%c All markers need the property "map_geolocation_lat" ', styleConsole);
                markerIsGeolocate = false;
                break;
            }

            if (
                ( element.hasOwnProperty('map_geolocation_lng') == false )
                || ( element['map_geolocation_lng'] == '' )
            ) {
                console.warn('%c All markers need the property "map_geolocation_lng" ', styleConsole);
                markerIsGeolocate = false;
                break;
            }
        }
        if( markerIsGeolocate == false )
            return false;

        return true;
    }

    /**
     * Check if the optional parameters are informed in and if not, set the default parameters
     */
    _checkDefaultParams() {

        /**
         Cluster Max Zoom
         **/
        if(
            ( this.data.hasOwnProperty('clusterMaxZoom') == false )
            || ( Number.isInteger( this.data['clusterMaxZoom'] ) == false )
        ) {
            this.data['clusterMaxZoom'] = this._params.clusterMaxZoomDefault;
        }

        /**
         Cluster Radius
         **/
        if(
            ( this.data.hasOwnProperty('clusterRadius') == false )
            || ( Number.isInteger( this.data['clusterRadius'] ) == false )
        ) {
            this.data['clusterRadius'] = this._params.clusterRadiusDefault;
        }

        /**
         Cluster Circle
         **/
        if( this.data.hasOwnProperty('clusterCircle') == false ) {
            this.data['clusterCircle'] = {};
        }
        if(
            ( this.data.clusterCircle.hasOwnProperty('color') == false )
            || ( this.data.clusterCircle['color'] == '' )
        ) {
            this.data.clusterCircle['color'] = this._params.clusterCircle.colorDefault;
        }
        if(
            ( this.data.clusterCircle.hasOwnProperty('radius') == false )
            || ( this.data.clusterCircle['radius'] == '' )
        ) {
            this.data.clusterCircle['radius'] = this._params.clusterCircle.radiusDefault;
        }
        if(
            ( this.data.clusterCircle.hasOwnProperty('stroke') == false )
            || ( typeof this.data.clusterCircle['stroke'] !== "object" )
        ) {
            this.data.clusterCircle['stroke'] = {};
        }

        if(
            ( this.data.clusterCircle.stroke.hasOwnProperty('width') == false )
            || ( this.data.clusterCircle.stroke['width'] == '' )
        ) {
            this.data.clusterCircle.stroke['width'] = this._params.clusterCircle.strokeDefault.width;
        }

        if(
            ( this.data.clusterCircle.stroke.hasOwnProperty('color') == false )
            || ( this.data.clusterCircle.stroke['color'] == '' )
        ) {
            this.data.clusterCircle.stroke['color'] = this._params.clusterCircle.strokeDefault.color;
        }

        /**
         Cluster Point
         **/
        if(
            ( this.data.clusterPoint.hasOwnProperty('size') == false )
            || ( Number.isInteger( this.data.clusterPoint['size'] ) == false )
        ) {
            this.data.clusterPoint['size'] = this._params.clusterPoint.sizeDefault;
        }

        /**
         Cluster Count
         **/
        if( this.data.hasOwnProperty('clusterCount') == false ) {
            this.data['clusterCount'] = {};
        }

        if(
            ( this.data.clusterCount.hasOwnProperty('size') == false )
            || ( Number.isInteger( this.data.clusterCount['size'] ) == false )
        ) {
            this.data.clusterCount['size'] = this._params.clusterCount.sizeDefault;
        }

        if(
            ( this.data.clusterCount.hasOwnProperty('color') == false )
            || ( this.data.clusterCount['color'] == '' )
        ) {
            this.data.clusterCount['color'] = this._params.clusterCount.colorDefault;
        }

        if(
            ( this.data.clusterCount.hasOwnProperty('font') )
            && ( this.data.clusterCount['font'] != '' )
        ) {
            this._params.clusterCount.fontDefault.unshift(this.data.clusterCount['font']);
        }
        this.data.clusterCount['font'] = this._params.clusterCount.fontDefault;
    }

    /**
     * Creating a geojson for the cluster
     */
    _initDataJSON() {

        this.geojson = {
            id: this.data.id,
            type: 'FeatureCollection',
            features: this._getMarkers( this.data.markers )
        };
    }

    /**
     * Create feature for all markers in the cluster
     * @param {Object} listMarkers - list of the markers with their properties
     * @return {Array} - Features of cluster
     */
    _getMarkers( listMarkers ) {

        let markersFormatted = [];

        for ( let marker in listMarkers ) {

            if ( listMarkers.hasOwnProperty( marker ) ) {

                let element = listMarkers[marker];
                let typeMarker = element.type || 'basic';

                let feature = {
                    type: typeMarker,
                    geometry: {
                        type: 'Point',
                        coordinates: [ element.map_geolocation_lng, element.map_geolocation_lat ]
                    },
                    properties: {}
                };

                for ( let attribute in element ) {

                    if( isNaN( element[attribute] ) || element[attribute] == '' ) {
                        feature.properties[attribute] = element[attribute];
                    } else {
                        feature.properties[attribute] = parseFloat( element[attribute] );
                    }
                }

                markersFormatted.push( feature );
            }
        }

        return markersFormatted;
    }
}

/*
    Class PopupManager
    Create popup in a google map that is displayed with each click on a pin
    Params : {}
*/
class PopupManager extends EventEmitter {

    constructor() {
        super();

        this._popup = new mapboxgl.Popup({closeOnClick: false});
        this._isMobile = (ENV.device == 'mobile' || ENV.device == 'tablet');

        this._bind();
    }

    _bind() {
        this._listeners = {};
        this._listeners._close = this._close.bind(this);

        this._popup.on('close', this._listeners._close );
    }

    /**
     * When the user clicks on a pin of the map
     * @param {Array} coordinates - Latitude and longitude of the popin clicked
     * @param {Node} contentHTML - the HTML content of the popin
     * @param {Map} map - the map that contains the popin clicked
     */
    open( coordinates, contentHTML, map ) {

        this._popup
            .setLngLat(coordinates)
            .setHTML(contentHTML)
            .addTo(map);

        /** Opening a popin over the page : we block the scroll of the page **/
        if( this._isMobile ) {
            this._currentScroll = window.pageYOffset;

            setTimeout(() => {
                document.body.classList.add('no-scroll');
            }, 600);
        }
    }

    /**
     * When the user clicks on the closing cross
     */
    _close() {
        this.emit( MANAGE_POPUP_EVENT.CLOSE );

        /** Close popin : we restore the position of the scroll on the page **/
        if( this._isMobile ) {
            document.body.classList.remove('no-scroll');
            TweenMax.set(window, { scrollTo: { y: this._currentScroll } });
        }
    }
}


// WEBPACK FOOTER //
// src/js/helpers/map-manager.js